import { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { parseCsvFile } from '../../features/wpRentalsImport/parseCsv';
import {
  validateHeaders,
  rowsToLongFormat,
  rowsToWideFormat,
  applyBaseUrlToImages,
  findPostIdColumn,
} from '../../features/wpRentalsImport/buildRecords';
import type { WpImportProperty } from '../../features/wpRentalsImport/types';
import { propertyToListingDraft } from '../../features/wpRentalsImport/mapListing';
import { runWpImportBatch } from '../../features/wpRentalsImport/runImport';
import {
  checkStorageMirrorImagesForProperties,
  type StorageMirrorImageCheckResult,
} from '../../features/wpRentalsImport/storageMirror';
import toast from 'react-hot-toast';

type Step = 'upload' | 'preview' | 'importing' | 'done';

export default function WPRentalsImport() {
  const { currentUser } = useAuth();
  const [step, setStep] = useState<Step>('upload');
  const [wpBaseUrl, setWpBaseUrl] = useState('');
  const [imageMode, setImageMode] = useState<'wp_fetch_upload' | 'storage_mirror'>('storage_mirror');
  const [storageUploadsPrefix, setStorageUploadsPrefix] = useState('uploads');
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [storageCheck, setStorageCheck] = useState<Record<string, StorageMirrorImageCheckResult>>({});
  const [storageChecking, setStorageChecking] = useState(false);
  const [headerIssues, setHeaderIssues] = useState<string[]>([]);
  const [properties, setProperties] = useState<WpImportProperty[]>([]);
  const [parseNote, setParseNote] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0, label: '' });
  const [summary, setSummary] = useState<Awaited<
    ReturnType<typeof runWpImportBatch>
  > | null>(null);

  const hostId = currentUser?.uid ?? '';

  const onFile = useCallback(async (file: File | null) => {
    if (!file) return;
    setHeaderIssues([]);
    setParseNote('');
    setSummary(null);
    setStorageCheck({});
    try {
      const { headers, rows } = await parseCsvFile(file);
      const { ok, missing } = validateHeaders(headers);
      if (!ok) {
        setHeaderIssues(missing);
        toast.error('CSV is missing required columns.');
        return;
      }
      const hasLong =
        headers.map((h) => h.toLowerCase()).includes('meta_key') &&
        headers.map((h) => h.toLowerCase()).includes('meta_value');
      let list: WpImportProperty[];
      if (hasLong) {
        const idCol = findPostIdColumn(headers);
        if (!idCol) {
          setHeaderIssues(['Could not find ID / post_id column']);
          return;
        }
        list = rowsToLongFormat(rows, headers);
        setParseNote(`Long format: ${list.length} properties from ${rows.length} meta rows.`);
      } else {
        list = rowsToWideFormat(rows);
        setParseNote(`Wide format: ${list.length} rows as properties.`);
      }
      const base = wpBaseUrl.trim();
      setProperties(
        list.map((p) => ({
          ...p,
          images: base ? applyBaseUrlToImages(p.images, base) : p.images,
        }))
      );
      setStep('preview');
      toast.success('CSV parsed. Review preview below.');
    } catch (e: unknown) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Failed to parse CSV');
    }
  }, [wpBaseUrl]);

  const verifyStorageImages = async () => {
    if (imageMode !== 'storage_mirror') {
      toast.error('Switch to Firebase Storage mirror mode first.');
      return;
    }
    if (!storageUploadsPrefix.trim()) {
      toast.error('Set Storage uploads prefix (e.g. "uploads").');
      return;
    }
    setStorageChecking(true);
    try {
      const rows = await checkStorageMirrorImagesForProperties(
        properties,
        storageUploadsPrefix.trim()
      );
      const byId: Record<string, StorageMirrorImageCheckResult> = {};
      for (const r of rows) byId[r.wpPostId] = r;
      setStorageCheck(byId);
      const totalChecked = rows.reduce((a, r) => a + r.checked, 0);
      const totalFound = rows.reduce((a, r) => a + r.found, 0);
      const anyMissing = rows.some((r) => r.missing.length > 0);
      toast.success(
        `Storage check: ${totalFound}/${totalChecked} image files found${
          anyMissing ? ' (some missing - hover “Storage” badge)' : ''
        }.`
      );
    } catch (e: unknown) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Storage check failed');
    } finally {
      setStorageChecking(false);
    }
  };

  const toggleRow = (wpPostId: string) => {
    setStorageCheck({});
    setProperties((prev) =>
      prev.map((p) =>
        p.wpPostId === wpPostId ? { ...p, selected: !p.selected } : p
      )
    );
  };

  const toggleImage = (wpPostId: string, imageId: string) => {
    setStorageCheck({});
    setProperties((prev) =>
      prev.map((p) => {
        if (p.wpPostId !== wpPostId) return p;
        const next = new Set(p.selectedImageIds);
        if (next.has(imageId)) next.delete(imageId);
        else next.add(imageId);
        return { ...p, selectedImageIds: next };
      })
    );
  };

  const previewRows = useMemo(
    () =>
      properties.map((p) => {
        const draft = hostId
          ? propertyToListingDraft(p, hostId, { skipContent: false })
          : null;
        return { p, draft };
      }),
    [properties, hostId]
  );

  const refreshImageUrls = () => {
    const b = wpBaseUrl.trim();
    if (!b) {
      toast.error('Set WordPress base URL first.');
      return;
    }
    setProperties((prev) =>
      prev.map((p) => ({
        ...p,
        images: applyBaseUrlToImages(
          p.images.map((i) => ({ ...i, url: i.raw || i.url })),
          b
        ),
      }))
    );
    toast.success('Image URLs refreshed from base URL + paths.');
  };

  const runImport = async () => {
    if (!hostId) {
      toast.error('You must be signed in.');
      return;
    }
    if (imageMode === 'wp_fetch_upload' && !wpBaseUrl.trim()) {
      toast.error('Set WordPress site base URL so relative image paths resolve.');
      return;
    }
    if (imageMode === 'storage_mirror' && !storageUploadsPrefix.trim()) {
      toast.error('Set Storage uploads prefix (e.g. "uploads").');
      return;
    }
    setStep('importing');
    setProgress({ current: 0, total: 0, label: 'Starting…' });
    try {
      const res = await runWpImportBatch(properties, {
        hostId,
        wpBaseUrl: wpBaseUrl.trim(),
        overwriteExisting,
        imageMode,
        storageUploadsPrefix: storageUploadsPrefix.trim(),
        onProgress: (pr) => setProgress({ current: pr.current, total: pr.total, label: pr.label }),
      });
      setSummary(res);
      setStep('done');
      toast.success(
        `Import finished: ${res.created} created, ${res.updated} updated, ${res.skipped} skipped.`
      );
    } catch (e: unknown) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Import failed');
      setStep('preview');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Import from WP Rentals</h1>
          <p className="text-gray-600 text-sm mt-1">
            Local / admin only: upload a CSV export (post + meta rows or wide columns). Images are
            either fetched from WordPress (CORS-dependent) or resolved from a Firebase Storage mirror
            (recommended for local WP sites).
          </p>
        </div>
        <Link to="/admin/listings" className="btn btn-secondary text-sm">
          Back to Listings
        </Link>
      </div>

      {step === 'upload' && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4 max-w-2xl">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              WordPress site base URL (for uploads paths)
            </label>
            <input
              className="input w-full"
              placeholder="https://example.com"
              value={wpBaseUrl}
              onChange={(e) => setWpBaseUrl(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">
              Used to resolve <code className="bg-gray-100 px-1">_wp_attached_file</code> paths to full
              URLs.
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
            <p className="font-medium mb-1">Need a template?</p>
            <a
              className="text-[#32373c] underline"
              href="/templates/listings-import-template.csv"
              download
            >
              Download generic listings CSV template
            </a>
            <p className="text-xs text-gray-600 mt-1">
              Leave <code className="bg-white px-1">id</code> blank to auto-generate during import.
              Images can be added manually later.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CSV file
            </label>
            <input
              type="file"
              accept=".csv,text/csv"
              className="block w-full text-sm text-gray-600"
              onChange={(e) => onFile(e.target.files?.[0] ?? null)}
            />
          </div>
          {headerIssues.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-medium mb-1">Missing or invalid headers:</p>
              <ul className="list-disc list-inside">
                {headerIssues.map((h) => (
                  <li key={h}>{h}</li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-xs text-gray-500">
            Required (long/meta format): <strong>ID</strong> (or post_id), <strong>meta_key</strong>,{' '}
            <strong>meta_value</strong>, plus <strong>post_title</strong> / <strong>post_content</strong>{' '}
            on rows where present. Wide format: <strong>post_title</strong> and arbitrary meta columns.
          </p>
        </div>
      )}

      {step === 'preview' && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow p-4 flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-700 mb-1">WP base URL</label>
              <input
                className="input w-full"
                value={wpBaseUrl}
                onChange={(e) => setWpBaseUrl(e.target.value)}
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Used only for <strong>WP fetch + upload</strong> mode.
              </p>
            </div>
            <div className="min-w-[220px]">
              <label className="block text-xs font-medium text-gray-700 mb-1">Image source</label>
              <select
                className="input w-full"
                value={imageMode}
                onChange={(e) => {
                  setImageMode(e.target.value as 'wp_fetch_upload' | 'storage_mirror');
                  setStorageCheck({});
                }}
              >
                <option value="storage_mirror">Firebase Storage mirror (uploads/…)</option>
                <option value="wp_fetch_upload">Fetch from WordPress + upload to Storage</option>
              </select>
              {imageMode === 'storage_mirror' ? (
                <p className="text-[11px] text-gray-500 mt-1">
                  Upload your WP <code className="bg-gray-100 px-1">wp-content/uploads</code> contents
                  to Storage under <code className="bg-gray-100 px-1">uploads/</code>.
                </p>
              ) : (
                <p className="text-[11px] text-gray-500 mt-1">
                  Browser fetch is often blocked by CORS on local WP sites.
                </p>
              )}
            </div>
            {imageMode === 'storage_mirror' && (
              <div className="min-w-[180px]">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Storage prefix
                </label>
                <input
                  className="input w-full"
                  value={storageUploadsPrefix}
                  onChange={(e) => {
                    setStorageUploadsPrefix(e.target.value);
                    setStorageCheck({});
                  }}
                  placeholder="uploads"
                />
              </div>
            )}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={overwriteExisting}
                onChange={(e) => setOverwriteExisting(e.target.checked)}
              />
              Overwrite existing <code className="text-xs">wp-&#123;id&#125;</code> listings
            </label>
            {imageMode === 'wp_fetch_upload' && (
              <button type="button" className="btn btn-secondary text-sm" onClick={refreshImageUrls}>
                Refresh image URLs
              </button>
            )}
            {imageMode === 'storage_mirror' && (
              <button
                type="button"
                className="btn btn-secondary text-sm"
                onClick={verifyStorageImages}
                disabled={storageChecking}
              >
                {storageChecking ? 'Checking Storage…' : 'Verify images in Storage'}
              </button>
            )}
            <button type="button" className="btn btn-primary" onClick={runImport}>
              Confirm import
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setStep('upload');
                setProperties([]);
              }}
            >
              New file
            </button>
          </div>
          <p className="text-sm text-gray-600">{parseNote}</p>

          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left">
                  <th className="p-2 w-10">On</th>
                  <th className="p-2">WP ID</th>
                  <th className="p-2">Title</th>
                  <th className="p-2">Flags</th>
                  <th className="p-2">Images</th>
                  <th className="p-2">Mapped preview</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map(({ p, draft }) => {
                  const sc = storageCheck[p.wpPostId];
                  return (
                  <tr key={p.wpPostId} className={`border-b ${p.selected ? '' : 'opacity-50'}`}>
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={p.selected}
                        onChange={() => toggleRow(p.wpPostId)}
                      />
                    </td>
                    <td className="p-2 font-mono text-xs">{p.wpPostId}</td>
                    <td className="p-2 font-medium">{p.postTitle}</td>
                    <td className="p-2">
                      <div className="flex flex-wrap gap-1">
                        {p.hadSerializedMeta && (
                          <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs">
                            PHP meta parsed
                          </span>
                        )}
                        {imageMode === 'storage_mirror' && sc && sc.checked > 0 && (
                          <span
                            className={`px-2 py-0.5 rounded text-xs ${
                              sc.missing.length === 0
                                ? 'bg-emerald-100 text-emerald-900'
                                : 'bg-amber-100 text-amber-900'
                            }`}
                            title={
                              sc.missing.length
                                ? `Missing in Storage:\n${sc.missing
                                    .slice(0, 8)
                                    .map((m) => m.objectPath)
                                    .join('\n')}${sc.missing.length > 8 ? '\n…' : ''}`
                                : 'All selected images exist at the expected Storage path.'
                            }
                          >
                            Storage {sc.found}/{sc.checked}
                          </span>
                        )}
                        {p.reviews.map((r) => (
                          <span
                            key={`${r.field}-${r.message}`}
                            className={`px-2 py-0.5 rounded text-xs ${
                              r.severity === 'error'
                                ? 'bg-red-100 text-red-800'
                                : r.severity === 'warn'
                                  ? 'bg-amber-100 text-amber-900'
                                  : 'bg-gray-100 text-gray-700'
                            }`}
                            title={r.message}
                          >
                            {r.field}
                          </span>
                        ))}
                        {draft?.reviews
                          .filter((x) => !p.reviews.some((y) => y.field === x.field))
                          .map((r) => (
                            <span
                              key={`d-${r.field}`}
                              className="px-2 py-0.5 rounded bg-amber-50 text-amber-900 text-xs"
                              title={r.message}
                            >
                              map:{r.field}
                            </span>
                          ))}
                      </div>
                    </td>
                    <td className="p-2 max-w-xs">
                      <div className="flex flex-wrap gap-1">
                        {p.images.map((im) => (
                          <label
                            key={im.id}
                            className="inline-flex flex-col items-center gap-1 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={p.selectedImageIds.has(im.id)}
                              onChange={() => toggleImage(p.wpPostId, im.id)}
                            />
                            {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                            <img
                              src={im.url}
                              alt=""
                              className="w-12 h-12 object-cover rounded border"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.opacity = '0.3';
                              }}
                            />
                          </label>
                        ))}
                        {p.images.length === 0 && (
                          <span className="text-gray-400 text-xs">No images detected</span>
                        )}
                      </div>
                    </td>
                    <td className="p-2 text-xs text-gray-700 max-w-md">
                      {draft && (
                        <ul className="list-disc list-inside space-y-0.5">
                          <li>
                            Price (cents): {draft.listing.basePrice} · Beds {draft.listing.bedrooms}{' '}
                            · Baths {draft.listing.bathrooms} · Sleeps {draft.listing.capacity}
                          </li>
                          <li>
                            {draft.listing.location.city},{' '}
                            {draft.listing.location.address.slice(0, 80)}
                          </li>
                          <li>Firestore doc: {draft.docId}</li>
                        </ul>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {step === 'importing' && (
        <div className="bg-white rounded-lg shadow p-8 max-w-lg">
          <p className="font-medium mb-2">Importing…</p>
          <p className="text-sm text-gray-600 mb-4">{progress.label}</p>
          <div className="h-2 bg-gray-200 rounded overflow-hidden">
            <div
              className="h-full bg-[#32373c] transition-all"
              style={{
                width:
                  progress.total > 0
                    ? `${Math.round((100 * progress.current) / progress.total)}%`
                    : '0%',
              }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {progress.current} / {progress.total}
          </p>
        </div>
      )}

      {step === 'done' && summary && (
        <div className="bg-white rounded-lg shadow p-6 max-w-xl space-y-3">
          <h2 className="text-xl font-semibold">Import complete</h2>
          <ul className="text-sm space-y-1 list-disc list-inside">
            <li>Created: {summary.created}</li>
            <li>Updated: {summary.updated}</li>
            <li>Skipped (duplicate): {summary.skipped}</li>
            <li>Images uploaded: {summary.imageUploaded}</li>
            <li>Images resolved from Storage: {summary.imageResolved}</li>
            <li>Images failed: {summary.imageFailed}</li>
          </ul>
          {summary.errors.length > 0 && (
            <div className="text-xs text-red-700 max-h-40 overflow-y-auto border rounded p-2">
              {summary.errors.slice(0, 30).map((e, i) => (
                <div key={i}>{e}</div>
              ))}
              {summary.errors.length > 30 && (
                <div>… and {summary.errors.length - 30} more</div>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <Link to="/admin/listings" className="btn btn-primary text-sm">
              View listings
            </Link>
            <button
              type="button"
              className="btn btn-secondary text-sm"
              onClick={() => {
                setStep('upload');
                setProperties([]);
                setSummary(null);
              }}
            >
              Import another
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
